import React, { RefObject, useEffect, useState } from "react";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import { ImageUpload } from "components/imageUpload";
import { useAppDispatch, useAppSelector } from "store";
import { getCtaEntities } from "store/cms/banners/slice";
import NoImage from "assets/images/image_not_found.webp";
import TextEditor from "components/wysiwyg/Wysiwyg";

const PAGE_OPTIONS = [
  { value: "sports", label: "Sports" },
  { value: "casino", label: "Casino" },
  { value: "main", label: "Main" },
  { value: "tournaments", label: "Tournaments" },
  { value: "bonuses", label: "Bonuses" },
  { value: "promo", label: "Promo" },
];

const CTA_TARGET_OPTIONS = [
  { value: "pwa", label: "PWA" },
  { value: "bonus", label: "Bonus" },
  { value: "collection", label: "Collection" },
  { value: "deposit", label: "Deposit" },
  { value: "loyalty", label: "Loyalty" },
  { value: "game", label: "Game" },
  { value: "promo", label: "Promo" },
  { value: "tag", label: "Tag" },
  { value: "tournament", label: "Tournament" },
  { value: "page", label: "Page" },
];

interface Props {
  t: (key: string) => string;

  activeItem: string;
  activeLocale?: string;
  payload: any;
  payloadList?: any[];
  payloadLocale: any;
  geoBannersList?: any[];
  localesList?: string[];
  errorfields: any;
  imgPreview: string;
  imgPreviewBig: string;
  clearFieldError: (field: string) => void;
  inputRef: RefObject<HTMLInputElement>;
  inputRef2: RefObject<HTMLInputElement>;

  setActiveItem: (v: string) => void;
  setActiveLocale: (v: string) => void;
  handleData: (key: string, value: any, img?: File) => void;
  handleDataLocale: (key: string, value: any, innerId: string) => void;
  fieldErrors?: Record<string, string>;
  IMAGE_SIZE: Record<string, string>;
  readOnly?: boolean;
}

const BannersBannersTab: React.FC<Props> = ({
  t,
  activeItem,
  activeLocale,
  payload,
  payloadList,
  payloadLocale,
  geoBannersList,
  localesList,
  errorfields,
  imgPreview,
  imgPreviewBig,
  setActiveItem,
  setActiveLocale,
  handleData,
  handleDataLocale,
  clearFieldError,
  IMAGE_SIZE,
  fieldErrors,
  readOnly,
}) => {
  const dispatch = useAppDispatch();

  const ctaTargets = useAppSelector((store) => store["cms-banners"].ctaTargets);
  const isCtaLoading = useAppSelector(
    (store) => store["cms-banners"].isCtaLoading,
  );

  const [search, setSearch] = useState("");
  const [bannerCount, setBannerCount] = useState(3);

  const targetOptions = CTA_TARGET_OPTIONS.filter(({ value }) =>
    payload?.ctaType === "redirect" ? value === "page" : value !== "page",
  );

  useEffect(() => {
    if (!payload?.ctaActionType) return;
    if (["deposit", "pwa", "loyalty", "page"].includes(payload.ctaActionType))
      return;

    const timeout = setTimeout(() => {
      dispatch(
        getCtaEntities({
          actionType: payload.ctaActionType,
          country: payload?.geo as string,
          search: search || undefined,
        }),
      );
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [payload?.geo, payload?.ctaActionType, search]);

  useEffect(() => {
    setSearch("");
  }, [payload?.ctaActionType]);

  const selectedItem = ctaTargets.find(
    (item) => item.value === payload?.ctaInstanceId,
  );

  const finalOptions =
    payload?.ctaActionType === "page"
      ? PAGE_OPTIONS
      : payload?.ctaActionType === "pwa"
        ? [
            {
              value: "pwa",
              label: "PWA installation flow",
            },
          ]
        : payload?.ctaActionType === "loyalty"
          ? [
              {
                value: "loyalty",
                label: "Loyalty/VIP page",
              },
            ]
          : payload?.ctaActionType === "deposit"
            ? []
            : search
              ? ctaTargets
              : selectedItem
                ? ctaTargets
                : payload?.ctaInstanceId
                  ? [
                      {
                        value: payload.ctaInstanceId,
                        label: payload.ctaSlug || "Selected item",
                        img: NoImage,
                      },
                      ...ctaTargets,
                    ]
                  : ctaTargets;

  const geoItems =
    payloadList?.filter((item) => item.geo === payload?.geo) || [];

  const bannerItems = Array.from(
    { length: Math.max((geoBannersList?.length || 0) + 1, 3) },
    (_, index) => `Item${index + 1}`,
  );

  return (
    <div className="banners-form-wrapper">
      <div className="game-modal-tabs banners-items-tabs small">
        {bannerItems.map((item, index) => (
          <div
            key={item}
            className={`tab-item ${activeItem === item ? "active" : ""}`}
            onClick={() => setActiveItem(item)}
          >
            {geoBannersList?.[index]?.id ? (
              <span className="banner-check exist">✔</span>
            ) : (
              <span className="banner-check add">+</span>
            )}
            Banner {index + 1}
          </div>
        ))}
      </div>

      {activeItem && (
        <div className="banners-form-content">
          <div className="banners-images-row">
            <div className="bonus-form-img banner-small">
              <div className="label">{t("imageSmall")}</div>

              <ImageUpload
                image={imgPreview}
                onAddImage={(file) => {
                  handleData("imageSmall", "", file);
                  clearFieldError("imageSmall");
                }}
                isActive={true}
                hasError={Boolean(fieldErrors?.imageSmall)}
                disabled={readOnly}
              />
            </div>

            <div className="bonus-form-img banner-big">
              <div className="label">
                {t("imageBig")}
                <span className="banner-size">
                  {geoBannersList?.length
                    ? IMAGE_SIZE[geoBannersList.length]
                    : IMAGE_SIZE[1]}
                </span>
              </div>

              <ImageUpload
                image={imgPreviewBig}
                onAddImage={(file) => {
                  handleData("imageBig", "", file);
                  clearFieldError("imageBig");
                }}
                isActive={true}
                hasError={Boolean(fieldErrors?.imageBig)}
                disabled={readOnly}
              />
            </div>
          </div>

          <div className="banners-form-item row banners-cta-row">
            <div className="cta-title">
              <DefaultDropdown
                value={payload?.ctaType}
                isBot
                onSelect={(e) => {
                  handleData("ctaType", e);

                  const hasIncompatibleTarget =
                    (e === "redirect" && payload?.ctaActionType !== "page") ||
                    (e !== "redirect" && payload?.ctaActionType === "page");

                  if (hasIncompatibleTarget) {
                    handleData("ctaActionType", undefined);
                    handleData("ctaInstanceId", undefined);
                    handleData("ctaSlug", undefined);
                    handleData("ctaProviderSlug", undefined);
                  }
                }}
                placeholder={t("ctaType")}
                label={t("ctaType")}
                options={[
                  { value: "redirect", label: "Redirect" },
                  { value: "button", label: t("button") },
                  { value: "link", label: t("link") },
                  { value: "text", label: t("text") },
                  { value: "full", label: t("full") },
                ]}
                disabled={readOnly}
              />

              <DefaultDropdown
                value={payload?.ctaActionType}
                isBot
                placeholder={t("target")}
                label="Target"
                options={targetOptions}
                onSelect={(value) => {
                  const actionType = String(value);

                  handleData("ctaActionType", actionType);

                  handleData("ctaInstanceId", undefined);
                  handleData("ctaSlug", undefined);
                  handleData("ctaProviderSlug", undefined);

                  if (
                    !["deposit", "pwa", "loyalty", "page"].includes(actionType)
                  ) {
                    dispatch(
                      getCtaEntities({
                        actionType,
                        country: payload?.geo as string,
                      }),
                    );
                  }
                }}
                disabled={readOnly}
              />
            </div>
            <div className="banners-form-cta">
              <DefaultDropdown
                value={
                  payload?.ctaActionType === "page"
                    ? payload?.ctaSlug
                    : payload?.ctaActionType === "pwa"
                      ? "pwa"
                      : payload?.ctaActionType === "loyalty"
                        ? "loyalty"
                        : payload?.ctaInstanceId
                }
                isBot
                placeholder={
                  payload?.ctaActionType === "loyalty"
                    ? "Loyalty/VIP page"
                    : ["deposit", "pwa"].includes(payload?.ctaActionType)
                      ? "No items available"
                      : "Choose Item"
                }
                label="Title"
                options={finalOptions}
                disabled={["deposit", "pwa", "loyalty"].includes(
                  payload?.ctaActionType,
                ) || readOnly}
                onSelect={(value) => {
                  if (payload?.ctaActionType === "page") {
                    handleData("ctaInstanceId", undefined);
                    handleData("ctaSlug", String(value));
                    handleData("ctaProviderSlug", undefined);
                    return;
                  }

                  const selected = ctaTargets.find(
                    (item) => item.value === value,
                  );

                  handleData("ctaInstanceId", selected?.value);
                  handleData("ctaSlug", selected?.slug);

                  if (payload?.ctaActionType === "game") {
                    handleData("ctaProviderSlug", selected?.providerSlug);
                  } else {
                    handleData("ctaProviderSlug", undefined);
                  }
                }}
                search={search}
                onSearch={setSearch}
                loading={isCtaLoading}
                
              />
            </div>
          </div>

          <div className="game-modal-tabs bonus-locales-tabs small">
            {localesList?.map((locale) => (
              <div
                key={locale}
                className={`tab-item ${
                  activeLocale === locale ? "active" : ""
                } ${
                  payload?.locales?.some((l: any) => l.locale === locale)
                    ? ""
                    : "inactive"
                }`}
                onClick={() => setActiveLocale(locale)}
              >
                <img
                  src={`https://flagpedia.net/data/flags/icon/16x12/${locale
                    .split("-")[1]
                    ?.toLowerCase()}.png`}
                  alt=""
                />
                {t(locale)}
              </div>
            ))}
          </div>

          <div className="banners-form-item row">
            <InnerInput
              hasError={errorfields?.title}
              value={payloadLocale?.title ?? ""}
              placeholder={t("title")}
              label={t("title")}
              type="text"
              id="banners-title"
              onChange={(e) => handleDataLocale("title", e, activeItem)}
              disabled={readOnly}
            />
            <InnerInput
              hasError={errorfields?.cta}
              value={payloadLocale?.cta ?? ""}
              placeholder={t("cta")}
              label={t("cta")}
              type="text"
              id="banners-cta"
              onChange={(e) => handleDataLocale("cta", e, activeItem)}
              disabled={readOnly}
            />
            <InnerInput
              value={payloadLocale?.note ?? ""}
              placeholder={t("note")}
              label={t("note")}
              type="text"
              id="banners-note"
              onChange={(e) => handleDataLocale("note", e, activeItem)}
              disabled={readOnly}
            />
          </div>
          <div className="banners-content-editor html-editor">
            <div className="label">{t("content")}</div>

            <TextEditor
              value={payloadLocale?.content ?? ""}
              onChange={(value) =>
                handleDataLocale("content", value, activeItem)
              }
              disabled={readOnly}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BannersBannersTab;
